import { flow, SkipMode } from '@1x2network/flow'
import { ParticleTrailSpecification } from '@1x2network/particle_trail'
import { Component, ContainerSpecification } from '@1x2network/pixi_assets'
import { IClickHandler } from '@1x2network/pixi_dialogue/lib/components/TieredPopup'
import { SymbolFetcher } from '@1x2network/reel_set/lib/composite/components/fetcher/SymbolFetcher'
import { Container } from 'pixi.js'
import { CustomSymbol } from '../CustomSymbol'
import { CollectionEvent } from '../model/HoldAndWinModel'
import { ShapeOptions } from '../types/ShapeOptions'
import { staggerTimeline } from '../utils/gsap'
import { createParticleTrail } from '../utils/createParticleTrail'
import { Signal } from 'typed-signals'
import gsap from 'gsap'
import { toSeconds } from '../utils/toSeconds'
import { gsapSpineWrapper, TweenVars } from '@1x2network/tweens'

/**
 * Animation that collects cash values from symbols and adds them to a collector symbol
 * @author Adam Drake - <adam.drake@1x2network.com>
 * @version 1.0.0
 */
export class Collection extends Component<CollectionSpecification> {
    private fetcher: SymbolFetcher<CustomSymbol>
    private clickHandler: IClickHandler

    public readonly onActivate = new Signal()
    public readonly onCollect = new Signal()

    constructor(options: {
        spec: string | CollectionSpecification
        parent?: Container
        fetcher: SymbolFetcher<CustomSymbol>
        clickHandler: IClickHandler
    }) {
        super(options)

        this.clickHandler = options.clickHandler
        this.fetcher = options.fetcher
    }

    public async animate(event: CollectionEvent): Promise<void> {
        const collector = this.fetcher.getSymbolByPosition(event.to)
        const connection = this.clickHandler.onClick.connect(() => flow.skipStart())
        const { timings } = this.specification
        const { wait, segment } = timings
        if (!collector.spine) {
            return
        }
        await flow.wait(wait.before, { skipMode: SkipMode.Skip })

        const timeline = gsap
            .timeline()

            // Animate the collector
            .add(gsapSpineWrapper(collector.spine, 0, collector.animations.activate))
            .call(
                () => {
                    this.onActivate.emit()
                },
                [],
                0,
            )

            // Collect the symbols
            .add(
                staggerTimeline(
                    event.items.map((item) => this.collectSymbol(collector, item)),
                    toSeconds(segment.stagger),
                ),
                toSeconds(wait.startSpawn),
            )

        await flow.wait(timeline, { skipMode: SkipMode.Skip })
        await flow.wait(wait.after, { skipMode: SkipMode.Skip })

        connection.disconnect()
        flow.skipStop()
    }

    private collectSymbol(collector: CustomSymbol, item: CollectionEvent['items'][number]): GSAPAnimation {
        const cashSymbol = this.fetcher.getSymbolByPosition(item.from)
        const startPosition = cashSymbol.position
        const targetPosition = collector.position

        const trail = createParticleTrail({
            startPosition: startPosition,
            endPosition: targetPosition,
            steps: this.specification.trailAnim.steps,
            shape: this.specification.trailAnim.shape,
            spec: this.specification.trailSpec,
            parent: this,
        })

        return (
            gsap
                .timeline()
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                .add(gsapSpineWrapper(cashSymbol.spine!, 0, cashSymbol.animations.activate))
                .add(() => {
                    cashSymbol.blurred = false
                })
                .add(
                    trail.animate({
                        ...this.specification.trailAnim.tweenVars,
                        onComplete: () => {
                            this.onCollect.emit()
                            collector.playCollect()
                            collector.updateTextValue(item.cumulative.currency)
                        },
                        onParticlesComplete: () => {
                            trail.destroy()
                        },
                    }),
                    toSeconds(this.specification.timings.segment.trailSpawn),
                )
        )
    }
}

export interface CollectionSpecification extends ContainerSpecification {
    trailSpec: ParticleTrailSpecification
    trailAnim: {
        shape: ShapeOptions
        steps: number
        tweenVars: TweenVars
    }
    timings: {
        wait: {
            before: number
            after: number
            startSpawn: number
        }
        segment: {
            trailSpawn: number
            stagger: number
        }
    }
}
